import { getFamilyPresentation, getStatePresentation } from './emotional-impact';
import { getModePresentation } from './modes';
import {
  resolveAtmosphere,
  resolveDimmedElements,
  resolveEmphasizedElements,
  resolveFunctionalProperties,
  resolveHiddenByUser,
  resolveMovementIntensity,
  resolveScopes,
} from './priority-matrix';
import type {
  AccessibilityPreferences,
  PresentationInput,
  PresentationPreferences,
  ResolvedPresentation,
} from './presentation-types';
import { familyOfState } from '../../../domain/emotion/enums';

const DEFAULT_PREFERENCES: PresentationPreferences = {
  showProgress: true,
  showEditorialCards: true,
  showTimes: true,
  locale: 'fr-FR',
};

const DEFAULT_ACCESSIBILITY: AccessibilityPreferences = {
  ags: false,
  reducedMotion: false,
};

/**
 * ASCEND V7 — résolution centralisée de présentation. Fonction pure : aucune dépendance à
 * React ou Dexie, aucune lecture de `localStorage`, aucun effet de bord, aucune mutation des
 * données sources (`modes.ts`/`emotional-impact.ts` ne sont jamais réassignées).
 *
 * Composition, dans cet ordre : base V7 → mode de contexte → impact émotionnel →
 * accessibilité → préférences utilisateur → résultat final. Chaque couche n'écrit que les
 * champs qu'elle possède légitimement (voir `priority-matrix.ts`) ; il n'y a donc jamais de
 * conflit d'écrasement entre couches malgré l'ordre de composition séquentiel.
 *
 * Toujours un retour complet : un mode inconnu retombe sur `normal`, un état sans famille
 * connue retombe sur « aucun état », des préférences ou une accessibilité absentes retombent
 * sur les valeurs par défaut sûres.
 */
export function resolvePresentation(input: PresentationInput): ResolvedPresentation {
  const mode = getModePresentation(input.mode);
  const statePresentation = input.state ? getStatePresentation(input.state) : null;
  const family = statePresentation ? familyOfState(statePresentation.state) : null;
  const familyPresentation = family ? getFamilyPresentation(family) : null;

  const prefs: PresentationPreferences = { ...DEFAULT_PREFERENCES, ...input.prefs };
  const a11y: AccessibilityPreferences = { ...DEFAULT_ACCESSIBILITY, ...input.a11y };

  const { exigence, density } = resolveFunctionalProperties(mode);
  const atmosphere = resolveAtmosphere(mode, familyPresentation);
  const dimmed = resolveDimmedElements(familyPresentation, a11y);
  const emphasized = resolveEmphasizedElements(familyPresentation);
  const movementIntensity = resolveMovementIntensity(familyPresentation, a11y);
  const hiddenByUser = resolveHiddenByUser(prefs);
  const scopes = resolveScopes(mode, familyPresentation, a11y);

  return {
    mode: mode.mode,
    state: statePresentation?.state ?? null,
    family: familyPresentation?.family ?? null,
    modeScopeClass: mode.scopeClass,
    emotionalScopeClass: familyPresentation?.scopeClass ?? null,
    scopes,
    heroImage: atmosphere.heroImageKey,
    heroImagePosition: atmosphere.heroImagePosition,
    icon: mode.icon,
    emotionalIcon: familyPresentation?.icon ?? null,
    accent: atmosphere.accent,
    light: atmosphere.light,
    density,
    exigence,
    movementIntensity,
    tone: atmosphere.tone,
    dimmed,
    emphasized,
    hiddenByUser,
    thoughtKey: atmosphere.thoughtKey,
    thought: atmosphere.thought,
    intentionKey: atmosphere.intentionKey,
    intention: atmosphere.intention,
    routineKeys: atmosphere.routineKeys,
    routines: atmosphere.routines,
    locale: prefs.locale,
    ags: a11y.ags,
    reducedMotion: a11y.reducedMotion,
    explanation: {
      modeReason: `Mode ${mode.label} : exigence ${exigence}, densité ${density} — jamais modifiées par l'état émotionnel.`,
      emotionalReason: familyPresentation
        ? `État « ${statePresentation?.label} » (famille ${familyPresentation.label}) : atmosphère remplacée, ${dimmed.length} élément(s) atténué(s)${emphasized.length ? `, ${emphasized.length} mis en avant` : ''}.`
        : "Aucun état émotionnel déclaré : l'atmosphère du mode s'applique seule.",
      accessibilityReason: a11y.ags
        ? 'AGS actif : atténuation annulée, mouvement figé, cibles agrandies.'
        : a11y.reducedMotion
          ? 'Mouvement réduit demandé : atmosphère figée.'
          : 'Aucune préférence d’accessibilité renforcée.',
      preferencesReason: hiddenByUser.length
        ? `Retiré explicitement par l'utilisateur : ${hiddenByUser.join(', ')}.`
        : "Aucun élément retiré par préférence utilisateur.",
    },
  };
}
